import React, { useState } from "react";
import PageContainer from "../../components/PageContainer";
import {
  ComposedChart,
  Area,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  Legend,
} from "recharts";
import { Download, TrendingUp, TrendingDown, DollarSign, ShoppingBag, Calendar, ChevronDown } from "lucide-react";

// ─── Dummy Data ───────────────────────────────────────────────────────────────

const PERIODS = [
  { label: "This Month", value: "this_month" },
  { label: "Last Month", value: "last_month" },
  { label: "This Quarter", value: "this_quarter" },
  { label: "This Year", value: "this_year" },
];

const summaryByPeriod = {
  this_month:    { revenue: 485000, cogs: 290000, grossProfit: 195000, expenses: 62000, netProfit: 133000 },
  last_month:    { revenue: 412000, cogs: 248000, grossProfit: 164000, expenses: 58000, netProfit: 106000 },
  this_quarter:  { revenue: 1320000, cogs: 792000, grossProfit: 528000, expenses: 185000, netProfit: 343000 },
  this_year:     { revenue: 5240000, cogs: 3144000, grossProfit: 2096000, expenses: 724000, netProfit: 1372000 },
};

const expenseBreakdown = [
  { label: "Rent",               amount: 25000 },
  { label: "Salaries",           amount: 20000 },
  { label: "Logistics & Delivery", amount: 8000 },
  { label: "Packaging",          amount: 4500 },
  { label: "Utilities",          amount: 2500 },
  { label: "Marketing",          amount: 2000 },
];

const trendData = [
  { month: "Mar", revenue: 380000, cogs: 228000, profit: 152000 },
  { month: "Apr", revenue: 412000, cogs: 247200, profit: 164800 },
  { month: "May", revenue: 395000, cogs: 237000, profit: 158000 },
  { month: "Jun", revenue: 460000, cogs: 276000, profit: 184000 },
  { month: "Jul", revenue: 428000, cogs: 256800, profit: 171200 },
  { month: "Aug", revenue: 485000, cogs: 290000, profit: 195000 },
];

const categoryData = [
  { category: "Whey Protein",  revenue: 182000, cogs: 109200, profit: 72800,  margin: 40.0 },
  { category: "Creatine",      revenue: 94000,  cogs: 56400,  profit: 37600,  margin: 40.0 },
  { category: "Pre-Workout",   revenue: 78000,  cogs: 46800,  profit: 31200,  margin: 40.0 },
  { category: "Vitamins",      revenue: 65000,  cogs: 39000,  profit: 26000,  margin: 40.0 },
  { category: "Mass Gainer",   revenue: 44000,  cogs: 26400,  profit: 17600,  margin: 40.0 },
  { category: "Others",        revenue: 22000,  cogs: 12200,  profit: 9800,   margin: 44.5 },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n) => "₹ " + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const pct = (val, total) => total ? ((val / total) * 100).toFixed(1) + "%" : "—";

// ─── P&L Statement helpers ────────────────────────────────────────────────────

const StatRow = ({ label, value, bold, indent, highlight, negative }) => (
  <div className={`flex justify-between items-center py-2 px-4 ${
    highlight ? (negative ? "bg-red-50 rounded-lg" : "bg-green-50 rounded-lg") : indent ? "pl-8" : ""
  }`}>
    <span className={`text-sm ${bold ? "font-semibold text-gray-900" : "text-gray-600"}`}>{label}</span>
    <span className={`text-sm font-semibold ${
      highlight ? (negative ? "text-red-600" : "text-green-700") : bold ? "text-gray-900" : "text-gray-700"
    }`}>{value}</span>
  </div>
);

const Divider = () => <div className="border-t border-dashed border-gray-200 my-1 mx-4" />;

const SectionLabel = ({ children }) => (
  <div className="px-4 pt-4 pb-1">
    <span className="text-xs font-bold text-[#172554] uppercase tracking-widest">{children}</span>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const ProfitLoss = () => {
  const [period, setPeriod] = useState("this_month");
  const [periodOpen, setPeriodOpen] = useState(false);

  const data = summaryByPeriod[period];
  const grossMargin = pct(data.grossProfit, data.revenue);
  const netMargin   = pct(data.netProfit,   data.revenue);
  const isProfit    = data.netProfit >= 0;
  const selectedLabel = PERIODS.find((p) => p.value === period)?.label;

  return (
    <div className="bg-slate-50 min-h-screen">

        {/* ── HEADER ── */}
        <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h1 className="text-xl font-bold text-slate-800">Profit & Loss</h1>
            <p className="text-sm text-slate-500 mt-1">
              Financial overview of your business
            </p>
          </div>
          <div className="flex items-center gap-3 pe-4">
            {/* Period Selector */}
            <div className="relative">
              <button
                onClick={() => setPeriodOpen((o) => !o)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50 shadow-sm transition"
              >
                <Calendar size={15} className="text-slate-400" />
                {selectedLabel}
                <ChevronDown size={14} className="text-slate-400" />
              </button>
              {periodOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-100 rounded-xl shadow-lg z-10 w-44 overflow-hidden">
                  {PERIODS.map((p) => (
                    <button
                      key={p.value}
                      onClick={() => { setPeriod(p.value); setPeriodOpen(false); }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition hover:bg-gray-50 ${
                        period === p.value ? "text-blue-700 font-semibold bg-blue-50" : "text-gray-700"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-md text-sm font-medium hover:bg-orange-600 transition">
              <Download size={15} />
              Export
            </button>
          </div>
        </div>

        {/* ── KPI CARDS — gradient style from SalesReports ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {/* Revenue */}
          <div className="relative p-5 text-white overflow-hidden bg-gradient-to-br from-blue-500 to-indigo-600 border-e border-b">
            <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40" />
            <p className="text-sm opacity-80 relative z-10 font-semibold uppercase tracking-wider">Total Revenue</p>
            <h2 className="text-2xl font-bold mt-2 relative z-10">{fmt(data.revenue)}</h2>
            <p className="text-sm opacity-80 mt-1 relative z-10 font-medium">Gross margin: {grossMargin}</p>
          </div>

          {/* COGS */}
          <div className="relative p-5 text-white overflow-hidden bg-gradient-to-br from-orange-500 to-amber-600 border-e border-b">
            <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40" />
            <p className="text-sm opacity-80 relative z-10 font-semibold uppercase tracking-wider">Cost of Goods</p>
            <h2 className="text-2xl font-bold mt-2 relative z-10">{fmt(data.cogs)}</h2>
            <p className="text-sm opacity-80 mt-1 relative z-10 font-medium">{pct(data.cogs, data.revenue)} of revenue</p>
          </div>

          {/* Gross Profit */}
          <div className="relative p-5 text-white overflow-hidden bg-gradient-to-br from-emerald-500 to-green-600 border-e border-b">
            <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40" />
            <p className="text-sm opacity-80 relative z-10 font-semibold uppercase tracking-wider">Gross Profit</p>
            <h2 className="text-2xl font-bold mt-2 relative z-10">{fmt(data.grossProfit)}</h2>
            <p className="text-sm opacity-80 mt-1 relative z-10 font-medium">Margin: {grossMargin}</p>
          </div>

          {/* Net Profit/Loss */}
          <div className={`relative p-5 text-white overflow-hidden border-b bg-gradient-to-br ${isProfit ? "from-cyan-500 to-sky-600" : "from-red-500 to-rose-600"}`}>
            <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40" />
            <p className="text-sm opacity-80 relative z-10 font-semibold uppercase tracking-wider">{isProfit ? "Net Profit" : "Net Loss"}</p>
            <h2 className="text-2xl font-bold mt-2 relative z-10">{fmt(Math.abs(data.netProfit))}</h2>
            <p className="text-sm opacity-80 mt-1 relative z-10 font-medium">Net margin: {netMargin}</p>
          </div>
        </div>

        {/* ── SUMMARY BAR — large font-mono numbers like SalesReports ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 bg-white border-b border-slate-200">
          <div className="py-8 px-6 border-b md:border-b-0 md:border-e border-slate-200 flex flex-col justify-center">
            <p className="text-xs md:text-[13px] font-bold text-slate-400 uppercase tracking-wider">Gross Sales</p>
            <h3 className="text-2xl md:text-3xl font-black text-slate-800 mt-2 font-mono">{fmt(data.revenue)}</h3>
          </div>
          <div className="py-8 px-6 border-b md:border-b-0 md:border-e border-slate-200 flex flex-col justify-center">
            <p className="text-xs md:text-[13px] font-bold text-slate-400 uppercase tracking-wider">Total Expenses (COGS + Ops)</p>
            <h3 className="text-2xl md:text-3xl font-black text-red-600 mt-2 font-mono">-{fmt(data.cogs + data.expenses)}</h3>
          </div>
          <div className="py-8 px-6 flex flex-col justify-center">
            <p className="text-xs md:text-[13px] font-bold text-[#172554] uppercase tracking-wider">Net {isProfit ? "Profit" : "Loss"}</p>
            <h3 className={`text-2xl md:text-3xl font-black mt-2 font-mono ${isProfit ? "text-green-600" : "text-red-600"}`}>
              {fmt(data.netProfit)}
            </h3>
          </div>
        </div>

        {/* ── CHARTS SIDE BY SIDE ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Revenue vs Profit trend */}
          <div className="bg-white border-b border-e p-4 flex flex-col">
            <div className="flex items-center justify-between mb-6 px-2">
              <h3 className="text-sm font-bold text-[#172554] uppercase tracking-wider">Revenue vs Profit Trend</h3>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-[#172554]" /><span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Revenue</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-[#22c55e]" /><span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Profit</span></div>
              </div>
            </div>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trendData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#172554" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#172554" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#22c55e" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 600 }} axisLine={{ stroke: "#f1f5f9" }} tickLine={false} dy={10} />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 600 }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    cursor={{ stroke: "#e2e8f0", strokeWidth: 1 }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="bg-[#172554] text-white rounded-lg px-4 py-3 shadow-2xl border border-white/10">
                          <p className="text-[10px] font-bold opacity-50 uppercase tracking-widest mb-1">{label}</p>
                          {payload.map((p) => (
                            <p key={p.dataKey} className="text-sm font-bold capitalize">
                              {p.name}: ₹{p.value.toLocaleString()}
                            </p>
                          ))}
                        </div>
                      );
                    }}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="none" fillOpacity={1} fill="url(#colorRevenue)" />
                  <Line type="monotone" dataKey="revenue" stroke="#172554" strokeWidth={3} dot={{ r: 4, fill: "#172554", strokeWidth: 2, stroke: "#fff" }} activeDot={{ r: 6, strokeWidth: 0 }} name="Revenue" />
                  <Area type="monotone" dataKey="profit" stroke="none" fillOpacity={1} fill="url(#colorProfit)" />
                  <Line type="monotone" dataKey="profit" stroke="#22c55e" strokeWidth={3} dot={{ r: 4, fill: "#22c55e", strokeWidth: 2, stroke: "#fff" }} activeDot={{ r: 6, strokeWidth: 0 }} name="Profit" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Category bar chart */}
          <div className="bg-white border-b p-4 flex flex-col">
            <div className="flex items-center justify-between mb-6 px-2">
              <h3 className="text-sm font-bold text-[#172554] uppercase tracking-wider">Profit by Category</h3>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{selectedLabel}</span>
            </div>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={categoryData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }} barCategoryGap="25%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="category" tick={{ fontSize: 9, fill: "#94a3b8", fontWeight: 600 }} axisLine={{ stroke: "#f1f5f9" }} tickLine={false} dy={10} />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 600 }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    cursor={{ fill: "#f8fafc" }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-2xl">
                          <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">{payload[0].payload.category}</p>
                          <p className="text-lg font-black text-[#172554]">
                            {fmt(payload[0].value)}{" "}
                            <span className="text-xs text-slate-300 font-normal tracking-wide">Profit</span>
                          </p>
                          <p className="text-xs text-slate-500 mt-1">Margin: {payload[0].payload.margin}%</p>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="profit" radius={[4, 4, 0, 0]} barSize={40}>
                    {categoryData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={index % 2 === 0 ? "#172554" : "#F69314"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* ── BOTTOM: P&L Statement + Category Table SIDE BY SIDE ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2">

          {/* P&L Statement */}
          <div className="bg-white border-b border-e">
            <div className="px-5 py-4 border-b border-slate-200">
              <h3 className="text-sm font-bold text-[#172554] uppercase tracking-wider">P&L Statement</h3>
              <p className="text-xs text-slate-400 mt-0.5">{selectedLabel}</p>
            </div>
            <div className="py-2">
              <SectionLabel>Revenue</SectionLabel>
              <StatRow label="Sales Revenue" value={fmt(data.revenue)} indent />
              <Divider />
              <StatRow label="Total Revenue" value={fmt(data.revenue)} bold />

              <SectionLabel>Cost of Goods Sold</SectionLabel>
              <StatRow label="Purchase Cost (COGS)" value={`(${fmt(data.cogs)})`} indent />
              <Divider />
              <StatRow label="Gross Profit" value={fmt(data.grossProfit)} bold highlight />
              <div className="px-4 pb-1"><span className="text-xs text-green-600 font-medium">Gross Margin: {grossMargin}</span></div>

              <SectionLabel>Operating Expenses</SectionLabel>
              {expenseBreakdown.map((e) => (
                <StatRow key={e.label} label={e.label} value={`(${fmt(e.amount)})`} indent />
              ))}
              <Divider />
              <StatRow label="Total Expenses" value={`(${fmt(data.expenses)})`} bold />

              <div className="mt-2 mx-4 mb-3"><Divider /></div>
              <StatRow label={isProfit ? "NET PROFIT" : "NET LOSS"} value={fmt(data.netProfit)} bold highlight negative={!isProfit} />
              <div className="px-4 pb-4">
                <span className={`text-xs font-medium ${isProfit ? "text-green-600" : "text-red-500"}`}>Net Margin: {netMargin}</span>
              </div>
            </div>
          </div>

          {/* Category Breakdown Table */}
          <div className="bg-white border-b">
            <div className="px-5 py-4 border-b border-slate-200">
              <h3 className="text-sm font-bold text-[#172554] uppercase tracking-wider">Category Breakdown</h3>
              <p className="text-xs text-slate-400 mt-0.5">Revenue, cost and profit by category</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left px-4 py-3 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Category</th>
                    <th className="text-right px-4 py-3 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Revenue</th>
                    <th className="text-right px-4 py-3 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Profit</th>
                    <th className="text-right px-4 py-3 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Margin</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {categoryData.map((row, i) => (
                    <tr key={i} className="hover:bg-slate-50 transition">
                      <td className="px-4 py-3 font-medium text-slate-800">{row.category}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{fmt(row.revenue)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-green-600">{fmt(row.profit)}</td>
                      <td className="px-4 py-3 text-right">
                        <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-bold bg-green-100 text-green-700">{row.margin}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-[#172554]/20 bg-blue-50">
                    <td className="px-4 py-3 font-black text-[#172554] text-sm">Total</td>
                    <td className="px-4 py-3 text-right font-black text-[#172554]">{fmt(categoryData.reduce((s, r) => s + r.revenue, 0))}</td>
                    <td className="px-4 py-3 text-right font-black text-green-700">{fmt(categoryData.reduce((s, r) => s + r.profit, 0))}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-bold bg-green-200 text-green-800">
                        {((categoryData.reduce((s, r) => s + r.profit, 0) / categoryData.reduce((s, r) => s + r.revenue, 0)) * 100).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-xs text-gray-400 py-4 text-center">
          * All figures are based on static demo data. Live data will reflect once API integration is complete.
        </p>

      </div>
  );
};

export default ProfitLoss;
